import React, { useState, useEffect } from "react";
import { useGetAllBrands, useCreateBrand, useUpdateBrand, useDeleteBrand } from "hooks";
import { useProfileContext } from "context";

// Basic version of the multi-select
function BasicLocationMultiSelect({ options, defaultValue, onValueChange }) {
  return (
    <select
      multiple
      style={{ width: "100%", height: "100px" }}
      value={defaultValue}
      onChange={(e) => {
        const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
        onValueChange(selected);
      }}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export const OrganisationBrands = () => {
  const { userProfile } = useProfileContext();
  const { data: brandsData, isLoading, error } = useGetAllBrands(userProfile?.organisation_id);

  // Keep all the same state management
  const [displayableBrands, setDisplayableBrands] = useState([]);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [editingBrands, setEditingBrands] = useState({});

  // Format locations - keep the same logic
  const formattedLocations = React.useMemo(() => {
    const defaultBrand = brandsData?.find((brand) => brand.is_default);
    if (defaultBrand && defaultBrand.locations && defaultBrand.locations.length > 0) {
      return defaultBrand.locations.map((loc) => ({
        value: loc.id.toString(),
        label: loc.name,
      }));
    }
    return [];
  }, [brandsData]);

  const allFormattedLocations = React.useMemo(() => {
    if (!brandsData) return [];
    const locationsMap = {};
    brandsData.forEach((brand) => {
      brand.locations.forEach((loc) => {
        locationsMap[loc.id] = loc.name;
      });
    });
    return Object.entries(locationsMap).map(([id, name]) => ({
      value: id.toString(),
      label: name,
    }));
  }, [brandsData]);

  // Keep all the same effect hooks and handlers
  useEffect(() => {
    if (brandsData) {
      if (brandsData.length === 1 && brandsData[0].is_default) {
        setShowEmptyState(true);
        setDisplayableBrands([]);
      } else {
        const filteredBrands = brandsData.filter((brand) => !brand.is_default);
        setShowEmptyState(false);
        setDisplayableBrands(filteredBrands);
        const initialEditingState = {};
        filteredBrands.forEach((brand) => {
          initialEditingState[brand.id] = {
            name: brand.name,
            selectedLocationIds: brand.locations.map((loc) => loc.id.toString()),
          };
        });
        setEditingBrands(initialEditingState);
      }
    }
  }, [brandsData]);

  // All the same mutation hooks
  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();
  const deleteBrand = useDeleteBrand();

  // Keep all handlers the same
  const handleOpenDialog = () => {
    setNewBrandName("");
    setSelectedLocations([]);
    setIsDialogOpen(true);
  };

  const handleAddBrand = () => {
    if (!newBrandName.trim()) return;
    createBrand.mutate({
      orgID: userProfile.organisation_id,
      data: {
        name: newBrandName,
        location_ids: selectedLocations.map((id) => parseInt(id)),
      },
    });
    setIsDialogOpen(false);
  };

  const handleOpenDeleteDialog = (brand) => {
    setBrandToDelete(brand);
    setDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!brandToDelete) return;
    try {
      setIsDeleting(true);
      await deleteBrand.mutateAsync(brandToDelete.id);
      setDeleteConfirmDialog(false);
    } catch (error) {
      console.error("Error deleting brand:", error);
    } finally {
      setIsDeleting(false);
      setBrandToDelete(null);
    }
  };

  const toggleCardExpand = (brandId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [brandId]: !prev[brandId],
    }));
  };

  const handleBrandNameChange = (brandId, newName) => {
    setEditingBrands((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        name: newName,
      },
    }));
  };

  const handleBrandLocationsChange = (brandId, locationIds) => {
    setEditingBrands((prev) => ({
      ...prev,
      [brandId]: {
        ...prev[brandId],
        selectedLocationIds: locationIds,
      },
    }));
  };

  const handleSaveChanges = (brandId) => {
    const editingBrand = editingBrands[brandId];
    updateBrand.mutate({
      brandId,
      data: {
        name: editingBrand.name,
        location_ids: editingBrand.selectedLocationIds.map((id) => parseInt(id)),
      },
    });
    setExpandedCards((prev) => ({
      ...prev,
      [brandId]: false,
    }));
  };

  // Loading state - very basic
  if (isLoading) {
    return <div style={{ textAlign: "center", padding: "50px" }}>Loading brands...</div>;
  }

  // Error state - very basic
  if (error) {
    return <div style={{ textAlign: "center", padding: "50px", color: "red" }}>Error loading brands</div>;
  }

  // Empty state - very basic
  if (showEmptyState) {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
          <h1>Brands</h1>
          <button onClick={handleOpenDialog}>+ Create brand</button>
        </div>

        <div style={{ textAlign: "center", padding: "50px" }}>
          <h3>No brands</h3>
          <p>Create brands to easily manage captive portals and networks across your locations.</p>
        </div>

        {/* Create brand modal - very basic */}
        {isDialogOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "4px",
                width: "500px",
              }}
            >
              <h3>Create Brand</h3>

              <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
                <div style={{ flex: 1 }}>
                  <label>Brand name</label>
                  <br />
                  <input type="text" value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="Enter brand name" style={{ width: "100%", padding: "5px" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label>Locations</label>
                  <br />
                  <BasicLocationMultiSelect options={formattedLocations} defaultValue={selectedLocations} onValueChange={setSelectedLocations} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button onClick={() => setIsDialogOpen(false)}>Cancel</button>
                <button onClick={handleAddBrand} disabled={!newBrandName.trim()}>
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular state with brands - very basic
  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
        <span>
          {displayableBrands.length} {displayableBrands.length === 1 ? "brand total" : "brands total"}
        </span>
        <button onClick={handleOpenDialog}>+ Create brand</button>
      </div>

      {/* {formattedLocations.length > 0 && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeeba",
            padding: "10px",
            marginBottom: "20px",
            maxWidth: "450px",
          }}
        >
          <span>
            ⓘ {formattedLocations.length} location{formattedLocations.length !== 1 ? "s" : ""} have not been assigned to a brand.
          </span>
        </div>
      )} */}

      {/* Brand Cards - very basic */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {displayableBrands.map((brand) => {
          const isExpanded = expandedCards[brand.id];
          const editingBrand = editingBrands[brand.id] || { name: brand.name, selectedLocationIds: [] };

          return (
            <div key={brand.id} style={{ border: "1px solid #ddd", padding: "10px" }}>
              {/* Card Header */}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <div>
                    <b>{brand.name}</b>
                  </div>
                  <div style={{ color: "#666", fontSize: "0.9em" }}>
                    {brand.locations.length} {brand.locations.length === 1 ? "location" : "locations"}
                  </div>
                </div>
                <div>
                  <span style={{ marginRight: "10px" }} onClick={() => handleOpenDeleteDialog(brand)}>
                    [⋮]
                  </span>
                  <span onClick={() => toggleCardExpand(brand.id)}>
                    {isExpanded ? "▲" : "▼"}
                  </span>
                </div>
              </div>

              {/* Expanded Card Content */}
              {isExpanded && (
                <div
                  style={{
                    borderTop: "1px solid #ddd",
                    marginTop: "10px",
                    paddingTop: "10px",
                  }}
                >
                  <div style={{ display: "flex", gap: "20px", marginBottom: "10px" }}>
                    <div style={{ flex: 1 }}>
                      <label>
                        <b>Name</b>
                      </label>
                      <br />
                      <input
                        type="text"
                        value={editingBrand.name}
                        onChange={(e) => handleBrandNameChange(brand.id, e.target.value)}
                        placeholder="Enter brand name"
                        style={{ width: "100%", padding: "5px" }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label>
                        <b>Locations</b>
                      </label>
                      <br />
                      <BasicLocationMultiSelect
                        options={allFormattedLocations}
                        defaultValue={editingBrand.selectedLocationIds}
                        onValueChange={(values) => handleBrandLocationsChange(brand.id, values)}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => handleSaveChanges(brand.id)} disabled={!editingBrand.name.trim()}>
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create brand modal */}
      {isDialogOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "4px",
              width: "500px",
            }}
          >
            <h3>Create Brand</h3>

            <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
              <div style={{ flex: 1 }}>
                <label>
                  <b>Brand name</b>
                </label>
                <br />
                <input type="text" value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)} placeholder="Enter brand name" style={{ width: "100%", padding: "5px" }} />
              </div>
              <div style={{ flex: 1 }}>
                <label>
                  <b>Locations</b>
                </label>
                <br />
                <BasicLocationMultiSelect options={formattedLocations} defaultValue={selectedLocations} onValueChange={setSelectedLocations} />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button onClick={() => setIsDialogOpen(false)}>Cancel</button>
              <button onClick={handleAddBrand} disabled={!newBrandName.trim()}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "4px",
              width: "400px",
            }}
          >
            <h3>Delete Brand</h3>
            <p>Are you sure you want to delete {brandToDelete?.name}? This action cannot be undone.</p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button onClick={() => setDeleteConfirmDialog(false)}>Cancel</button>
              <button onClick={handleConfirmDelete} disabled={isDeleting} style={{ color: "red" }}>
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
